import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Label,
  ModalContent,
  Textarea,
  useUploadFile,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  ConductCaseCategory,
  ConductCaseSeverity,
  SubmitAcademicConductAppealDocument,
  UploadScope,
} from '@sandtech-hub-monorepo/ehub-client';
import { Info, Upload } from 'lucide-react';
import { ReactComponent as FileXIcon } from '../../../../assets/Icons/file-x.svg';

const APPEAL_TEMPLATE_URL =
  'https://docs.google.com/document/d/15oAcLQ7xRyvZCaD9MsW07a-L5rHPj6HgRmm-cipouI8/edit?tab=t.0';

interface Props {
  closeModal: () => void;
  caseId: string;
  severity: ConductCaseSeverity;
  category: ConductCaseCategory;
}

const SEVERITY_LABELS: Record<ConductCaseSeverity, string> = {
  [ConductCaseSeverity.ScholasticWarning]: 'Academic Warning',
  [ConductCaseSeverity.AcademicProbation]: 'Academic Probation',
  [ConductCaseSeverity.AcademicSuspension]: 'Academic Suspension',
  [ConductCaseSeverity.AcademicDismissal]: 'Enrollment withdrawn',
};

const CATEGORY_LABELS: Record<ConductCaseCategory, string> = {
  [ConductCaseCategory.GpaBelowMinimum]: 'GPA Below Minimum',
  [ConductCaseCategory.AcademicIntegrityViolation]:
    'Academic Integrity Violation',
};

const formSchema = z.object({
  reason: z.string().min(1, 'Please describe your reason for appealing'),
  signature: z.string().min(1, 'Please type your full name as signature'),
  confirmed: z.literal(true, {
    errorMap: () => ({ message: 'You must confirm' }),
  }),
});

type FormData = z.infer<typeof formSchema>;

export const AppealFormModal = ({
  closeModal,
  caseId,
  severity,
  category,
}: Props) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const { uploadFile, loading: uploadLoading } = useUploadFile();

  const [submitAppeal, { loading: submitLoading }] = useMutation(
    SubmitAcademicConductAppealDocument,
    {
      refetchQueries: ['GetViewerProgramEnrollmentsWithApplication'],
      onCompleted: () => {
        closeModal();
      },
      onError: (error) => {
        console.error('Failed to submit appeal:', error);
      },
    }
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reason: '',
      signature: '',
      confirmed: undefined,
    },
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setUploadedFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
    },
  });

  const handleSubmit = async (data: FormData) => {
    let documentUrl: string | undefined;
    let documentName: string | undefined;

    if (uploadedFile) {
      documentUrl = await uploadFile(
        UploadScope.ConductCaseDocument,
        uploadedFile
      );
      if (!documentUrl) return;
      documentName = uploadedFile.name;
    }

    submitAppeal({
      variables: {
        input: {
          caseId,
          reason: data.reason,
          signature: data.signature,
          documentUrl,
          documentName,
        },
      },
    });
  };

  const isLoading = uploadLoading || submitLoading;

  return (
    <ModalContent>
      <div className="flex flex-col gap-6 max-w-[500px] mx-auto">
        <div className="flex flex-col items-center">
          <div className="p-5 bg-heading/20 rounded-full text-heading mb-4">
            <Info height="48" width="48" />
          </div>
          <h2 className="text-heading font-semibold text-2xl mb-2 text-center">
            Make an Appeal
          </h2>
          <p className="text-heading text-sm text-center">
            You are about to appeal your{' '}
            <span className="font-semibold">{SEVERITY_LABELS[severity]}</span>.
          </p>
        </div>

        <div className="flex items-start gap-3 px-4 py-3 border border-destructive bg-destructive/10 rounded-lg text-destructive">
          <FileXIcon className="shrink-0 h-6 w-6" />
          <p className="text-sm">
            <span className="font-semibold">{SEVERITY_LABELS[severity]}:</span>{' '}
            {CATEGORY_LABELS[category]}
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-1.5">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Reason for appeal *
                  </Label>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Describe your reason for appealing"
                      className="min-h-[100px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-medium text-muted-foreground">
                Supporting document (optional)
              </Label>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-md px-4 py-6 flex flex-col items-center gap-2 cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="h-5 w-5 text-muted-foreground" />
                {uploadedFile ? (
                  <span className="text-sm text-foreground">
                    {uploadedFile.name}
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    {isDragActive
                      ? 'Drop file here'
                      : 'Drag and drop or click to upload (PDF, PNG, JPG)'}
                  </span>
                )}
              </div>
              <p className="text-sm text-foreground mt-1">
                Don't know how to appeal? Download appeal template{' '}
                <Link
                  to={APPEAL_TEMPLATE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-medium"
                >
                  Here&gt;
                </Link>
              </p>
            </div>

            <FormField
              control={form.control}
              name="signature"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-1.5">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Signature (full name) *
                  </Label>
                  <FormControl>
                    <Input
                      {...field}
                      type="text"
                      placeholder="Type your full name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmed"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-start gap-3">
                    <FormControl>
                      <Checkbox
                        checked={field.value === true}
                        onCheckedChange={(checked) =>
                          field.onChange(checked === true ? true : undefined)
                        }
                        className="mt-0.5"
                      />
                    </FormControl>
                    <span className="text-sm text-foreground leading-normal">
                      I confirm that the information provided in this appeal is
                      accurate and complete to the best of my knowledge.
                    </span>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-3 justify-end mt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => closeModal()}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!form.formState.isValid || isLoading}
              >
                {isLoading ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </ModalContent>
  );
};
